import React, { useContext, useState, useEffect } from 'react';
import Api from 'api';
import { storage, observer } from 'store';
import { FormGrid, FormField, FormRow, FormColumn, FormSubmit } from 'components/forms';
import { Input } from 'components/UI';
import withForm from 'components/forms/withForm';
import { ErrorsShakeHubContext, findFirstFieldWithError } from 'components/forms/utils';
import { corporateFormSchema } from 'components/forms/validationSchemas';
import { pushCustomSubmitEvent } from 'utils';
import { getCountryCode } from 'utils/localised';
import { phoneCode } from 'define/locale';

import * as styles from './CorporateForm.module.scss';

const Form = ({
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  formName,
  id,
  className,
  AdditionalInformationForForm,
  submitTitle,
  fullWidthButton,
  buttonClassName,
}) => {
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);

  useEffect(() => {
    return () => storage.addItem('isEmailCheckedSuccessful', false);
  }, []);

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <FormRow sizes={['sm12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.name} />
          <FormField
            Component={Input}
            {...fields.email}
            isLoading={storage.getItem('isEmailCheckLoading')}
            isChecked={storage.getItem('isEmailCheckedSuccessful')}
          />
          <FormField Component={Input} {...fields.phone} />
          <FormField Component={Input} {...fields.company} />
          <FormField Component={Input} {...fields.message} />
          <FormField Component={Input} {...fields.linkedIn} />
        </FormColumn>
      </FormRow>
      {AdditionalInformationForForm}
      <div className={styles.submitWrap}>
        <FormSubmit
          title={submitTitle ?? 'Зарегистрироваться'}
          nda={fields.nda}
          photoConsent={fields.photoConsent}
          disabled={isSubmitting}
          fullWidth={fullWidthButton}
          buttonClassName={buttonClassName}
          size="sm"
        />
      </div>
    </FormGrid>
  );
};

export default function CorporateForm({
  id = 'future_of_enterprise_transformation_tashkent',
  formName = 'future_of_enterprise_transformation_tashkent',
  closeModal,
  onSuccess,
  additionalFormData,
  className,
  getAdditionalFormData,
  AdditionalInformationForForm = '',
  inModal = false,
  withNda = true,
  withPhotoConsent = true,
  fullWidthButton,
  buttonClassName,
  submitTitle,
}) {
  const fields = {
    name: {
      initialValue: '',
      domProps: {
        placeholder: 'Имя и фамилия *',
        maxLength: 255,
      },
    },
    email: {
      initialValue: '',
      inModal,
      domProps: {
        placeholder: 'Рабочий email *',
        type: 'email',
        maxLength: 254,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue,
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: 'Номер телефона *',
        type: 'tel',
      },
    },
    company: {
      initialValue: '',
      domProps: {
        maxLength: 255,
        placeholder: 'Название компании *',
      },
    },
    message: {
      initialValue: '',
      domProps: {
        maxLength: 500,
        placeholder: 'Должность *',
      },
    },
    linkedIn: {
      initialValue: '',
      domProps: {
        maxLength: 128,
        placeholder: 'Профиль в LinkedIn',
      },
    },
  };

  if (withNda) {
    fields.nda = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'Я хочу сохранить конфиденциальность моих персональных данных.',
    };
  }

  if (withPhotoConsent) {
    fields.photoConsent = {
      initialValue: false,
      inModal,
      withoutError: true,
      content:
        'Я согласен с тем, что могу быть сфотографирован в ходе мероприятия, а также с использованием таких фото в рекламных материалах.',
    };
  }

  const errorsMessages = {
    name: 'Пожалуйста, укажите ваше полное имя',
    email: {
      required: 'Пожалуйста, введите вашу рабочую электронную почту',
      notCorporateEmail: 'Пожалуйста, введите вашу рабочую электронную почту',
      check: 'Неверный e-mail',
    },
    phone: 'Пожалуйста, введите номер для связи',
    company: 'Пожалуйста, укажите название компании, которую вы представляете',
    message: 'Пожалуйста, укажите вашу должность',
    linkedIn: {
      checkUrl: 'Введите корректную ссылку на LinkedIn',
      required: 'Пожалуйста, введите адрес вашей страницы в LinkedIn',
    },
    emoji: 'Эмодзи не разрешены',
    specialSymbols: 'Специальные символы не разрешены',
  };

  const defaultOnSuccess = () => storage.addItem('isEmailCheckedSuccessful', false);

  const Component = withForm({
    fields,
    validation: props => corporateFormSchema(errorsMessages, props),
    submit: {
      id,
      formName,
      handler: Api.sendForm.bind(Api, 'integration/form-data'),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess: onSuccess ?? defaultOnSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => pushCustomSubmitEvent(id),
      onResponse: () => {
        if (inModal) {
          closeModal();
        }
      },
    },
    formName,
    id,
    className,
    AdditionalInformationForForm,
    submitTitle,
    fullWidthButton,
    buttonClassName,
  })(observer(Form));

  return <Component />;
}

function getInitialValue() {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
}
